<template>
  <div class="ledger-page pb-5 pb-md-2">
    <b-card
      v-if="canRun"
      class="rounded-lg"
    >
      <h4 class="mb-1">
        {{ $t('ledger_rec_title') }}
      </h4>
      <b-row>
        <b-col
          cols="6"
          md="3"
        >
          <b-form-group :label="$t('ledger_payroll_year')">
            <b-form-select
              v-model.number="year"
              :options="yearOptions"
            />
          </b-form-group>
        </b-col>
        <b-col
          cols="6"
          md="3"
        >
          <b-form-group :label="$t('ledger_payroll_month')">
            <b-form-select
              v-model.number="month"
              :options="monthOptions"
            />
          </b-form-group>
        </b-col>
        <b-col
          cols="12"
          md="3"
          class="d-flex align-items-end mb-1"
        >
          <b-button
            variant="outline-primary"
            class="rounded-pill"
            block
            :disabled="previewing"
            @click="loadPreview"
          >
            {{ $t('ledger_rec_preview') }}
          </b-button>
        </b-col>
      </b-row>
      <b-alert
        v-if="preview && preview.already_posted"
        show
        variant="info"
      >
        {{ $t('ledger_rec_already') }}
        <b-link
          v-if="preview.run_id"
          :to="{ name: 'ledger-contract-recognition-view', params: { id: preview.run_id } }"
        >
          {{ $t('ledger_depr_open_run') }}
        </b-link>
      </b-alert>
      <template v-if="preview && !preview.already_posted">
        <div class="d-flex justify-content-between mb-1">
          <strong>{{ $t('ledger_rec_revenue') }}</strong>
          <span class="ledger-num">{{ money(preview.total_revenue) }}</span>
        </div>
        <div class="d-none d-md-block">
          <b-table
            :items="preview.lines || []"
            :fields="lineFields"
            small
            responsive
            class="ledger-dense-table"
          >
            <template #cell(percent_complete)="data">
              <span class="ledger-num">{{ percentLabel(data.item.percent_complete) }}</span>
            </template>
            <template #cell(revenue_this_period)="data">
              <span
                class="ledger-num"
                :class="{ 'text-danger': Number(data.item.revenue_this_period) < 0 }"
                :title="Number(data.item.revenue_this_period) < 0 ? $t('ledger_rec_estimate_change') : ''"
              >
                {{ money(data.item.revenue_this_period) }}
              </span>
            </template>
            <template #cell(wip_transferred)="data">
              <span class="ledger-num">{{ money(data.item.wip_transferred) }}</span>
            </template>
          </b-table>
        </div>
        <div class="d-md-none">
          <article
            v-for="line in preview.lines || []"
            :key="line.project_contract_id"
            class="ledger-ct-card"
          >
            <div>{{ line.contract_number }}</div>
            <div
              class="ledger-num"
              :class="{ 'text-danger': Number(line.revenue_this_period) < 0 }"
            >
              {{ money(line.revenue_this_period) }}
            </div>
          </article>
        </div>
        <div
          v-if="(preview.journal || []).length"
          class="mt-1"
        >
          <h6>{{ $t('ledger_depr_journal') }}</h6>
          <div
            v-for="(line, index) in preview.journal"
            :key="index"
            class="d-flex justify-content-between ledger-num"
          >
            <span>{{ journalLabel(line) }}</span>
            <span>{{ $t('ledger_debit') }} {{ money(line.debit) }} · {{ $t('ledger_credit') }} {{ money(line.credit) }}</span>
          </div>
        </div>
        <b-button
          v-if="(preview.lines || []).length"
          variant="primary"
          class="rounded-pill mt-1"
          :disabled="running"
          @click="confirmRun"
        >
          {{ $t('ledger_rec_post') }}
        </b-button>
      </template>
    </b-card>

    <b-card class="rounded-lg">
      <h5>{{ $t('ledger_depr_runs') }}</h5>
      <div
        v-if="loading"
        class="text-center py-3"
      >
        <b-spinner />
      </div>
      <div
        v-else-if="!runs.length"
        class="text-muted"
      >
        {{ $t('ledger_rec_empty') }}
      </div>
      <b-table
        v-else
        :items="runs"
        :fields="runFields"
        small
        responsive
        class="ledger-dense-table"
      >
        <template #cell(period)="data">
          <b-link :to="{ name: 'ledger-contract-recognition-view', params: { id: data.item.id } }">
            {{ data.item.year }}-{{ String(data.item.month).padStart(2, '0') }}
          </b-link>
        </template>
        <template #cell(total_revenue)="data">
          <span class="ledger-num">{{ money(data.item.total_revenue) }}</span>
        </template>
        <template #cell(status)="data">
          <b-badge :variant="enumValue(data.item.status) === 'posted' ? 'success' : 'dark'">
            {{ enumLabel(data.item.status, $i18n.locale) }}
          </b-badge>
        </template>
        <template #cell(actions)="data">
          <b-button
            v-if="canRun && enumValue(data.item.status) === 'posted'"
            size="sm"
            variant="outline-danger"
            @click="confirmReverse(data.item)"
          >
            {{ $t('ledger_rec_reverse') }}
          </b-button>
        </template>
      </b-table>
    </b-card>
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BFormGroup, BFormSelect, BButton, BAlert, BLink, BTable, BBadge, BSpinner,
} from 'bootstrap-vue'
import Swal from 'sweetalert2'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import {
  getRecognitionRuns, previewRecognition, createRecognitionRun, reverseRecognitionRun,
} from '@/api/accounting/contractRecognition'
import {
  listFrom, oneFrom, enumValue, enumLabel, formatLedgerMoney, apiErrorMessage,
} from '@/utils/ledger'

function previousMonth() {
  const date = new Date()
  date.setDate(1)
  date.setMonth(date.getMonth() - 1)
  return { year: date.getFullYear(), month: date.getMonth() + 1 }
}

export default {
  name: 'ContractRecognition',
  components: {
    BCard, BRow, BCol, BFormGroup, BFormSelect, BButton, BAlert, BLink, BTable, BBadge, BSpinner,
  },
  data() {
    const previous = previousMonth()
    return {
      year: previous.year,
      month: previous.month,
      preview: null,
      previewing: false,
      running: false,
      loading: false,
      runs: [],
    }
  },
  computed: {
    canRun() {
      return this.hasPermission('run contract_recognition')
    },
    now() {
      const date = new Date()
      return { year: date.getFullYear(), month: date.getMonth() + 1 }
    },
    yearOptions() {
      const years = []
      for (let year = this.now.year; year >= this.now.year - 6; year -= 1) years.push(year)
      return years
    },
    monthOptions() {
      const loc = this.$i18n.locale === 'en' ? 'en-EG' : 'ar-EG'
      return Array.from({ length: 12 }, (_, index) => {
        const month = index + 1
        const future = this.year > this.now.year || (this.year === this.now.year && month > this.now.month)
        return {
          value: month,
          text: new Intl.DateTimeFormat(loc, { month: 'long' }).format(new Date(2020, index, 1)),
          disabled: future,
        }
      })
    },
    lineFields() {
      return [
        { key: 'contract_number', label: this.$t('ledger_ct_number') },
        { key: 'project_name', label: this.$t('ledger_payroll_project') },
        { key: 'percent_complete', label: this.$t('ledger_ct_percent') },
        { key: 'revenue_this_period', label: this.$t('ledger_rec_revenue') },
        { key: 'wip_transferred', label: this.$t('ledger_rec_wip') },
      ]
    },
    runFields() {
      const fields = [
        { key: 'period', label: this.$t('ledger_payroll_period') },
        { key: 'total_revenue', label: this.$t('ledger_rec_revenue') },
        { key: 'contract_count', label: this.$t('ledger_rec_count') },
        { key: 'status', label: this.$t('ledger_status') },
      ]
      if (this.canRun) fields.push({ key: 'actions', label: this.$t('ledger_actions') })
      return fields
    },
  },
  watch: {
    year() {
      this.clampMonth()
      this.preview = null
    },
    month() {
      this.preview = null
    },
  },
  mounted() {
    this.loadRuns()
  },
  methods: {
    enumValue,
    enumLabel,
    money(value) {
      return formatLedgerMoney(value || 0, this.$i18n.locale)
    },
    percentLabel(value) {
      const n = Number(value)
      if (!Number.isFinite(n)) return '—'
      return `${(n * 100).toFixed(2)}%`
    },
    toast(title, variant) {
      this.$toast({
        component: ToastificationContent,
        props: { title, icon: variant === 'success' ? 'CheckIcon' : 'AlertCircleIcon', variant },
      })
    },
    clampMonth() {
      const future = this.year > this.now.year || (this.year === this.now.year && this.month > this.now.month)
      if (future) this.month = this.year === this.now.year ? this.now.month : 12
    },
    async loadRuns() {
      this.loading = true
      try {
        const res = await getRecognitionRuns()
        this.runs = listFrom(res)
      } catch (error) {
        this.toast(apiErrorMessage(error, this.$t('ledger_error')), 'danger')
      } finally {
        this.loading = false
      }
    },
    async loadPreview() {
      if (!this.canRun) return
      this.previewing = true
      try {
        const res = await previewRecognition({ year: this.year, month: this.month })
        this.preview = oneFrom(res)
      } catch (error) {
        this.preview = null
        this.toast(apiErrorMessage(error, this.$t('ledger_error')), error?.response?.status === 422 ? 'warning' : 'danger')
      } finally {
        this.previewing = false
      }
    },
    journalLabel(line) {
      const name = this.$i18n.locale === 'en'
        ? (line.name_en || line.name || '')
        : (line.name || line.name_en || '')
      return name ? `${line.code} ${name}` : String(line.code || '')
    },
    async confirmRun() {
      const journal = (this.preview.journal || []).map(line => {
        const label = this.journalLabel(line)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
        return `${label}: ${this.$t('ledger_debit')} ${this.money(line.debit)} / ${this.$t('ledger_credit')} ${this.money(line.credit)}`
      }).join('<br>')
      const result = await Swal.fire({
        title: this.$t('ledger_rec_post'),
        html: `<div>${this.$t('ledger_rec_confirm')}</div><div class="mt-1">${journal}</div>`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('ledger_rec_post'),
        cancelButtonText: this.$t('ledger_cancel'),
        customClass: { confirmButton: 'btn btn-primary', cancelButton: 'btn btn-outline-secondary ml-1' },
        buttonsStyling: false,
      })
      if (!result.isConfirmed) return
      this.running = true
      try {
        const res = await createRecognitionRun({ year: this.year, month: this.month })
        const status = res && res.status
        this.toast(status === 200 ? this.$t('ledger_rec_already') : this.$t('ledger_rec_posted'), status === 200 ? 'info' : 'success')
        this.preview = null
        await this.loadRuns()
      } catch (error) {
        this.toast(apiErrorMessage(error, this.$t('ledger_error')), error?.response?.status === 422 ? 'warning' : 'danger')
      } finally {
        this.running = false
      }
    },
    async confirmReverse(run) {
      const result = await Swal.fire({
        text: this.$t('ledger_rec_reverse_confirm'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('ledger_rec_reverse'),
        cancelButtonText: this.$t('ledger_cancel'),
        customClass: { confirmButton: 'btn btn-danger', cancelButton: 'btn btn-outline-secondary ml-1' },
        buttonsStyling: false,
      })
      if (!result.isConfirmed) return
      try {
        await reverseRecognitionRun(run.id)
        this.toast(this.$t('ledger_rec_reversed'), 'success')
        await this.loadRuns()
      } catch (error) {
        this.toast(apiErrorMessage(error, this.$t('ledger_error')), error?.response?.status === 422 ? 'warning' : 'danger')
      }
    },
  },
}
</script>

<style scoped>
.ledger-num { font-variant-numeric: tabular-nums; }
.ledger-ct-card { border-bottom: 1px solid #eee; padding: 0.75rem 0; }
</style>
